import React from 'react';
import { useNavigate } from 'react-router-dom';
import CommonButton from '../Components/CommonButton';
import PageHeading from '../Components/PageHeading';
import { Heading, SubHeading, Body } from '../Constants/Fonts';
import './MainDashboard.css';

// Product illustrations live as static SVGs in public/assets (served from the
// app root). Resolve them relative to PUBLIC_URL so they load under any basename.
const asset = (file) => `${process.env.PUBLIC_URL}/assets/${file}`;

const ArrowRight = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="13 6 19 12 13 18" />
    </svg>
);

const CheckMark = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="4 12 10 18 20 6" />
    </svg>
);

// The four platform modules, in display order.
const PRODUCTS = [
    {
        key: 'easm', index: '01', category: 'Exposure', title: 'EASM',
        subtitle: 'External Attack Surface Management',
        description: 'Discover, monitor and manage your external attack surface across the internet, cloud and third-party assets.',
        features: ['Asset discovery', 'Cloud & internet coverage', 'Threat exposure'],
        cta: 'Open EASM', route: '/dashboard?view=easm', img: asset('01-easm.svg'), topBg: '#f4f7fb',
    },
    {
        key: 'tpcrm', index: '02', category: 'Supply Chain', title: 'TPCRM',
        subtitle: 'Third Party Cyber Risk Management',
        description: 'Assess, monitor and manage third-party risks to strengthen your supply chain and reduce vulnerabilities.',
        features: ['Vendor assessment', 'Supply chain visibility', 'Continuous monitoring'],
        cta: 'Open TPCRM', route: '/tpcrm/dashboard', img: asset('02-tpcrm.svg'), topBg: '#e9eff9',
    },
    {
        key: 'ai', index: '03', category: 'Responsible AI', title: 'AI Governance',
        subtitle: 'Responsible & Compliant AI',
        description: 'Ensure responsible, secure and compliant use of AI across your organization with end-to-end governance.',
        features: ['AI policy', 'Secure AI use', 'End-to-end monitoring'],
        cta: 'Open AI Governance', route: '/coming-soon', img: asset('03-ai-governance.svg'), topBg: '#dfe9f5',
    },
    {
        key: 'compliance', index: '04', category: 'Assurance', title: 'Continuous Compliance',
        subtitle: 'Continuous Monitoring & Compliance',
        description: 'Simplify compliance with automated controls, continuous monitoring and real-time regulatory insight.',
        features: ['Automated controls', 'Real-time insights', 'Regulatory coverage'],
        cta: 'Open Compliance', route: '/dashboard?view=compliance', img: asset('04-compliance.svg'), topBg: '#d6e1f1',
    },
];

export default function MainDashboard({ userName = 'there', onOpenProduct }) {
    const navigate = useNavigate();
    const open = (p) => {
        if (onOpenProduct) onOpenProduct(p.key, p.route);
        else navigate(p.route);
    };

    return (
        <div className="md_root theme-light cmn_elevated_card">
            <div className="md_shell">
                <PageHeading
                    title="Dashboard"
                    subtitle="Welcome to your Foresee Security platform - manage every line of defence from one place."
                />

                {/* Hero */}
                <section className="md_hero">
                    <div className="md_hero_grid" aria-hidden="true" />
                    <Heading as="h1" className="md_hero_title" color="var(--color-text-primary)">
                        One platform. <span className="md_hero_title_accent">Four lines of defence.</span>
                    </Heading>
                    <Body className="md_hero_sub" color="var(--color-text-secondary)">
                        Manage risk, ensure compliance and build a stronger, safer tomorrow.
                    </Body>
                    <div className="md_status_pill">
                        <span className="md_status_dot" />
                        <Body as="span" variant={2} weight="medium" color="var(--color-text-primary)">Continuous monitoring active</Body>
                    </div>
                </section>

                {/* Products */}
                <div className="md_products_head">
                    <Heading variant={2} as="h2" className="md_section_title" color="var(--color-text-primary)">Products</Heading>
                    <Body as="span" className="md_eyebrow" color="var(--color-text-muted)">04 Modules · One platform</Body>
                </div>

                <div className="md_grid">
                    {PRODUCTS.map((p) => (
                        <article key={p.key} className="md_card cmn_elevated_card" onClick={() => open(p)} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open(p)}>
                            <div className="md_card_top" style={{ background: p.topBg }}>
                                <Body as="span" className="md_eyebrow" color="var(--color-primary)">{p.category}</Body>
                                <div className="md_card_icon"><img className="md_icon" src={p.img} alt="" aria-hidden="true" /></div>
                            </div>
                            <div className="md_card_body">
                                <SubHeading variant={2} as="h3" className="md_card_title" color="var(--color-text-primary)">{p.title}</SubHeading>
                                <Body as="span" variant={2} weight="semibold" className="md_card_subtitle" color="var(--color-text-secondary)">{p.subtitle}</Body>
                                <Body className="md_card_desc" color="var(--color-text-secondary)">{p.description}</Body>
                                <div className="md_divider" />
                                <ul className="md_features">
                                    {p.features.map((f) => (
                                        <li key={f} className="md_feature">
                                            <span className="md_feature_check"><CheckMark /></span>
                                            <Body as="span" variant={2} color="var(--color-text-primary)">{f}</Body>
                                        </li>
                                    ))}
                                </ul>
                                <CommonButton
                                    variant="primary"
                                    className="md_cta_btn"
                                    onClick={() => open(p)}
                                    text={<span className="md_cta_inner">{p.cta}<ArrowRight /></span>}
                                />
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}
